import type { OptionDB } from "@project/emerge";
import groupBy from "lodash/groupBy";
import mapValues from "lodash/mapValues";

import { HETomm } from "../buildsteps/FlightCaseBlueprintCalculator";
import { Covers } from "../enum/Covers";
import { InteriorType } from "../enum/InteriorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { RackCoverPaddingStyle } from "../enum/RackCoverPaddingStyle";
import type { CoverPaddingSettings } from "../interfaces/CoverPaddingSettings";
import type { CutProperties } from "../interfaces/CutProperties";
import type { Rack19InteriorOptions } from "../interfaces/Rack19InteriorOptions";
import type { RackInteriorOptions } from "../interfaces/RackInteriorOptions";
import type { RackRailType } from "../interfaces/RackRailType";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import { MissingOptionError } from "../user_options/MissingOptionError";

export type RackWidth19InchType = 465;
export const RackWidth19Inch: RackWidth19InchType = 465;

export const createCoverOptions = (
  optionDb: OptionDB,
  cuts: CutProperties[],
  areaPaddingType = "nopad",
  borderPaddingType = "nopad",
): { [groupName: string]: CoverPaddingSettings } => {
  return mapValues(
    groupBy(cuts, "groupName"),
    (): CoverPaddingSettings => ({
      areaPaddingStyle: RackCoverPaddingStyle.FULL,
      areaPaddingType: optionDb.getByName(
        LTCaseUserOption.PaddingType,
        areaPaddingType,
      ),
      borderPaddingType: optionDb.getByName(
        LTCaseUserOption.PaddingType,
        borderPaddingType,
      ),
      uuid: crypto.randomUUID(),
      width: 0,
    }),
  );
};

export const createRack19Interior = (
  optionDb: OptionDB,
  rackUnits = 10,
  depth = 500,
  panelStrength = 9,
): Rack19InteriorOptions => {
  const rackRails = optionDb.getByName<RackRailType>(
    LTCaseUserOption.RackRailType,
    "STW6162",
  );
  return {
    depth,
    height: HETomm(rackUnits),
    rackRailDisabled: {
      [Covers.FRONT_COVER]: false,
    },
    rackRailOffsets: {
      [Covers.TOP_COVER]: 0,
    },
    rackRails,
    standupRackRailOnTop: false,
    standupRackRails: rackRails,
    type: InteriorType.RACK19,
    underlayStrength: rackRails.forceUnderlayStrength || 15.5 - panelStrength,
    upperPaddingAlignment: false,
    width: RackWidth19Inch,
  };
};

/** The tray article of a rack interior; it must exist in the option database. */
export const RACK_TRAY_MATERIAL_NAME = "PLW1418";

/** @throws {MissingOptionError} when the database lacks the tray article. */
export const createRackInterior = (
  optionDb: OptionDB,
  height = 400,
  depth = 500,
  width = 500,
): RackInteriorOptions => {
  const trayMaterial = optionDb.get<TrayMaterialType>(
    LTCaseUserOption.TrayMaterialType,
    { name: RACK_TRAY_MATERIAL_NAME },
  );
  if (!trayMaterial) {
    throw new MissingOptionError(
      LTCaseUserOption.TrayMaterialType,
      RACK_TRAY_MATERIAL_NAME,
    );
  }
  return {
    depth,
    heights: [height],
    rackCoverPaddings: {},
    trayMaterial,
    type: InteriorType.RACK,
    upperPaddingAlignment: false,
    width,
    widths: [[width]],
  };
};
